import {Injectable, Injector} from '@angular/core';

import {Observable, Subject} from 'rxjs';
import {Project} from './project';
import {cacheResult, HttpService} from 'projects/core/src/public-api';
import {tap} from 'rxjs/operators';
import {TrelloCheckItem} from '../../../trello-misc/data/trello-check-item';
import {ProjectResponcePage} from './project-responce-page';
import {ProjectsStore} from './projects.store';
import {AppStateStore} from 'projects/desktop/src/app/data/app-state.store';
import {environment} from 'projects/desktop/src/environments/environment';
import {AppStateQuery} from 'projects/desktop/src/app/data/app-state.query';
import {WsService} from '../../../data/ws.service';
import {ProjectAlert} from '../../../alert/data/alert';
import {ProjectFile} from './project-file';
import {FileService} from 'projects/core/src/lib/misc/data/file/file.service';
import {HttpEvent} from '@angular/common/http';
import {ProjectComment} from './project-comment';
import {ProjectStatus} from './statuses/project-status';
import {EntityEventEmitter} from 'projects/core/src/lib/core/data/entities/entity-event-emitters';
import {Memoize} from 'projects/core/src/lib/core/utils/decorators/memoize-decorator';
import {ShortProject} from './short-project';
import {DbxPath} from './dbx-path';
import {SearchBody} from '../../../search/data/searchBody';
import {ShortObject} from './short-json-object';


@Injectable({
  providedIn: 'root',
})
export class ProjectService extends HttpService {
  projectEmitter = new EntityEventEmitter<Project>();

  checkListClickOutsideSubj = new Subject<any>();

  private saveCheckItemSubj = new Subject<TrelloCheckItem>();

  get saveCheckItem$(): Observable<TrelloCheckItem> {
    return this.saveCheckItemSubj
  }

  constructor(
    protected injector: Injector,
    protected projectsStore: ProjectsStore,
    protected appStateStore: AppStateStore,
    protected appStateQuery: AppStateQuery,
    protected fileService: FileService,
    protected wsService: WsService
  ) {
    super(injector);
  }

  loadProjects(start: number, limit: number): Observable<ProjectResponcePage> {
    return this.doHttpGet<ProjectResponcePage>(`/project/list?start=${start}&limit=${limit}`)
      .pipe(
        tap(page => {
          this.projectsStore.set(page.content)
          this.projectsStore.updateTotalElements(page.totalElements)
          this.projectsStore.setLoading(true);
        })
      )
  }

  loadSortProjects(start: number, limit: number, asc: boolean, label: string): Observable<ProjectResponcePage> {
    return this.doHttpGet<ProjectResponcePage>(`/project/list?start=${start}&limit=${limit}&asc=${asc}&sortBy=${label}`)
      .pipe(
        tap(page => {
          this.projectsStore.set(page.content)
          this.projectsStore.updateTotalElements(page.totalElements)
          this.projectsStore.setLoading(true);
        })
      )
  }

  createProject(): Observable<Project> {
    return this.putData(`/project/create`, {}, Project)
      .pipe(
        tap(createdProject => {
          this.projectEmitter.emitAdd(createdProject);
        })
      )
  }

  getProject(projectId: number): Observable<Project> {
    return this.getData(`/project/${projectId}`, Project);
  }

  getProjectByNumber(projectNumber: string): Observable<Project> {
    return this.getData(`/project/by-number/${projectNumber}`, Project);
  }

  getShortProject(projectId: number): Observable<Project> {
    return this.getData(`/project/${projectId}/schedule-info`, Project);
  }

  getProjectByCardId(cardId: number): Observable<Project> {
    return this.getData(`/column/all/projects/by-card-id/${cardId}`, Project);
  }

  getProjectAlerts(projectId: number): Observable<ProjectAlert[]> {
    return this.getData(`/alert/list/project/${projectId}`, ProjectAlert);
  }

  getProjectFiles(projectId: number): Observable<ProjectFile[]> {
    return this.getData(this.fileService.getListFilesLink('project', projectId), ProjectFile);
  }

  getProjectComments(projectId: number): Observable<ProjectComment[]> {
    return this.getData(`/project/${projectId}/comment/list`, ProjectComment);
  }

  getProjects(page: number, limit: number): Observable<ProjectResponcePage> {
    return this.getData(`/project/list?start=${page}&limit=${limit}`, ProjectResponcePage);
  }

  getLastProjectsForClockIn(): Observable<ShortProject[]> {
    return this.getData(`/project/list/for-clockin`, ShortProject);
  }

  getLastProjects(): Observable<Project[]> {
    return this.getData(`/project/list/in-progress`, Project);
  }

  getShortLastProjects(): Observable<ShortProject[]> {
    return this.getData(`/project/list/in-progress`, ShortProject);
  }

  getProjectsForShopTv(): Observable<Project[]> {
    return this.getData('/project/list/shop-tv', Project);
  }

  getProjectsForShopMachining(): Observable<Project[]> {
    return this.getData('/project/list/shop/for-machining', Project);
  }

  getProjectsForShopAssembly(): Observable<Project[]> {
    return this.getData('/project/list/shop/for-assembly', Project);
  }

  // getProjectsListForAssembly(): Observable<Project[]> {
  //   return this.getData('/project/list/for-assembly', Project);
  // }

  getProjectsForProductionQueue(): Observable<Project[]> {
    return this.getData('/project/list/shop/upcoming', Project);
  }

  getProjectsForPunchList(): Observable<Project[]> {
    return this.getData('/project/list/shop/has-active-punchlist', Project);
  }

  getProjectsForShopDelivery(): Observable<Project[]> {
    return this.getData('/project/list/shop/for-delivery', Project);
  }

  getProjectsForShopFinishing(): Observable<Project[]> {
    return this.getData('/project/list/shop/for-finishing', Project);
  }

  // @Memoize()
  getComplexities(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/PROJECT_COMPLEXITY`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  getPaymentStatuses(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/PAYMENT_STATUS`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  getDocusignStatuses(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/DOCUSIGN_STATUS`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  getInstallationPaymentStatuses(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/INSTALLATION_PAYMENT_STATUS`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }


  @Memoize()
  getProjectStatuses(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/PROJECT_STATUS`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  @Memoize()
  getProjectTypes(): Observable<ProjectStatus[]> {
    return this
      .getData(`/dictionary-word/list/for-type/PROJECT_TYPE`, ProjectStatus)
      .pipe(
        cacheResult(environment.dataDefaultCacheTime)
      );
  }

  getProjectDropBoxLink(projectId: number): Observable<DbxPath> {
    return this.getData(`/project/${projectId}/dbx-path`, DbxPath)
  }

  syncDropboxPath(projectId: number): Observable<DbxPath> {
    return this.putData(`/project/${projectId}/sync-dbx-files`, {}, DbxPath);
  }

  sendToSeattleProd(projectId: number): Observable<Project> {
    return this.putData(`/project/${projectId}/migrate`, {}, Project);
  }

  changeDropboxPath(projectid: number, dropboxObj): Observable<DbxPath> {
    return this.putData(`/project/${projectid}/dbx-path`, dropboxObj, DbxPath);
  }

  saveProject(project: Project): Observable<Project> {
    if (project.projectManager) {
      project.projectManager = {id: project.projectManager.id};
    }
    return this.putData('/project/update', project, Project)
      .pipe(
        tap(updateProject => {
          this.projectEmitter.emitUpdate(updateProject);
        }))
  }

  toggleActivity(project: Project, activityType: string, status: boolean): Observable<Project> {
    return this.putData(`/project/${project.id}/activities/${activityType}/status?value=${status}`, { status }, Project)
      .pipe(
        tap(() => {
          if ((project as any).hasOwnProperty(activityType + 'Ready')) {
            project[activityType + 'Ready'] = status
          }
          this.projectEmitter.emitUpdate(project);
        }))
  }

  saveCheckItem(checkItem: TrelloCheckItem): Observable<TrelloCheckItem> {
    return this.putData('/checklist/checkitem/update', checkItem, TrelloCheckItem)
      .pipe(
        tap(item => {
          this.saveCheckItemSubj.next(item);
        })
      )
  }

  searchProjects(query: string): Observable<ShortObject[]> {
    return this.postData(`/project/list/search`, {
      query
    } as SearchBody, ShortObject);
  }

  copyProject(projectId: number, names: string[]): Observable<ShortProject[]> {
    return this.postData(`/project/${projectId}/copy`, names, ShortProject)
  }

  // tslint:disable-next-line: ban-types
  addComment(projectId: number, comment: ProjectComment): Observable<ProjectComment> {
    return this.postData(`/project/${projectId}/comment`, comment, ProjectComment)
  }

  // tslint:disable-next-line: ban-types
  saveComment(projectId: number, commentId: number, comment: ProjectComment): Observable<ProjectComment> {
    return this.putData(`/project/${projectId}/comment/${commentId}`, comment, ProjectComment)
  }

  deleteComment(projectId: number, commentId: number): Observable<ProjectComment> {
    return this.deleteData(`/project/${projectId}/comment/${commentId}`, ProjectComment)
  }

  uploadFile(files: File[], projectId: number): Observable<HttpEvent<ProjectFile[]>> {
    return this.fileService.postFile<ProjectFile[]>(this.fileService.getUploadFileLink('project', projectId), 'files', files);
  }

  deleteFile(fileId: number, id: number): Observable<ProjectFile> {
    return this.deleteData(this.fileService.getDeleteFileLink(fileId), ProjectFile)
  }

  getRandomProject(): Observable<Project> {
    return this.getData(`/project/random`, Project)
  }

  detachCustomer(projectId: number): Observable<Project> {
    return this.putData(`/project/${projectId}/detach-customer`, {}, Project)
  }

  atachUser(projectId: number, userId: number): Observable<Project> {
    return this.postData(`/project/${projectId}/participant/${userId}`, {}, Project)
      .pipe(
        tap(project => {
          this.projectEmitter.emitUpdate(project);
        }))
  }

  detachUser(projectId: number, userId: number): Observable<Project> {
    return this.deleteData(`/project/${projectId}/participant/${userId}`, Project)
      .pipe(
        tap(project => {
          this.projectEmitter.emitUpdate(project);
        }))
  }

  deleteProjectCVSync(projectId: number): Observable<void> {
    return this.deleteData(`/project/${projectId}/cv-data`, Project);
  }

  deleteProject(projectId: number): Observable<void> {
    return this.deleteData(`/project/${projectId}/delete`, Project)
      .pipe(
        tap(() => {
          this.projectEmitter.emitRemove(projectId);
        })
      )
  }

  getShortPricingByProjectId(projectId: number): Observable<number> {
    return this.doHttpGet(`/pricing/id-for-project/${projectId}`);
    // return this.getData(`/pricing/id-for-project/${projectId}`, Number);
  }

}
